import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { DropdownMenu, DropdownToggle, UncontrolledDropdown, DropdownItem } from "reactstrap";
import { Modal, ModalBody, Form } from "reactstrap";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    address: "",
    city: "",
    country: "Congo Brazzaville",
    is_active: true,
  });

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    setLoading(true);
    try {
      const response = await api.getSites();
      setSites(response.data || response || []);
    } catch (error) {
      console.error("Erreur lors du chargement des sites:", error);
      toast.error("Erreur lors du chargement des sites");
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      address: "",
      city: "",
      country: "Congo Brazzaville",
      is_active: true,
    });
  };

  const onFormCancel = () => {
    setModal(false);
    setEditId(null);
    resetForm();
  };

  const onFormSubmit = async (submitData) => {
    try {
      // Convertir is_active en boolean
      const data = {
        ...submitData,
        is_active: submitData.is_active === true || submitData.is_active === 'true' || submitData.is_active === 'on',
      };
      
      if (editId) {
        await api.updateSite(editId, data);
        toast.success("Site modifié avec succès");
      } else {
        await api.createSite(data);
        toast.success("Site créé avec succès");
      }
      loadSites();
      onFormCancel();
    } catch (error) {
      let errorMessage = "Erreur lors de l'enregistrement du site";
      
      if (error.errors) {
        // Erreurs de validation du backend
        const errorsList = Object.values(error.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errorsList = Object.values(error.response.data.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  const onEditClick = (id) => {
    const site = sites.find((s) => s.id === id);
    if (site) {
      setFormData({
        name: site.name || "",
        code: site.code || "",
        address: site.address || "",
        city: site.city || "",
        country: site.country || "Congo Brazzaville",
        is_active: site.is_active !== undefined ? site.is_active : true,
      });
      setEditId(id);
      setModal(true);
    }
  };

  const onDeleteClick = (id) => {
    const site = sites.find((s) => s.id === id);
    if (site) {
      setDeleteId(id);
      setDeleteName(site.name || "ce site");
      setDeleteModal(true);
    }
  };

  const onDeleteConfirm = async () => {
    if (!deleteId) return;
    
    try {
      await api.deleteSite(deleteId);
      toast.success("Site supprimé avec succès");
      loadSites();
      setDeleteModal(false);
      setDeleteId(null);
      setDeleteName("");
    } catch (error) {
      const errorMessage = error.message || "Erreur lors de la suppression du site";
      toast.error(errorMessage);
    }
  };

  const onDeleteCancel = () => {
    setDeleteModal(false);
    setDeleteId(null);
    setDeleteName("");
  };

  const selectorCheck = (e) => {
    const newData = sites.map((item) => {
      item.checked = e.currentTarget.checked;
      return item;
    });
    setSites([...newData]);
  };

  const onSelectChange = (e, id) => {
    const newData = sites;
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].checked = e.currentTarget.checked;
      setSites([...newData]);
    }
  };

  // Filtrer les sites selon le texte de recherche
  const filteredSites = sites.filter((site) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      (site.name && site.name.toLowerCase().includes(searchLower)) ||
      (site.code && site.code.toLowerCase().includes(searchLower)) ||
      (site.city && site.city.toLowerCase().includes(searchLower)) ||
      (site.country && site.country.toLowerCase().includes(searchLower)) ||
      (site.address && site.address.toLowerCase().includes(searchLower))
    );
  });

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredSites.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Réinitialiser la page quand la recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const { errors, register, handleSubmit } = useForm();

  return (
    <React.Fragment>
      <Head title="Sites & Zones" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Sites & Zones</BlockTitle>
              <BlockDes className="text-soft">
                <p>
                  {searchText
                    ? `${filteredSites.length} site(s) trouvé(s) sur ${sites.length}`
                    : `Vous avez ${sites.length} site(s) au total`}
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <div className="form-control-wrap">
                    <div className="form-icon form-icon-left">
                      <Icon name="search"></Icon>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher un site..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </li>
                <li>
                  <Button color="primary" size="md" onClick={() => setModal(true)}>
                    <Icon name="plus"></Icon>
                    <span>Nouveau site</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner">
                <div className="text-center py-5">Chargement...</div>
              </div>
            </div>
          ) : filteredSites.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner">
                <div className="text-center py-5">
                  <p className="text-soft">
                    {searchText ? "Aucun site ne correspond à votre recherche" : "Aucun site trouvé"}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow className="nk-tb-col-check">
                      <div className="custom-control custom-control-sm custom-checkbox notext">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="site-all"
                          onChange={(e) => selectorCheck(e)}
                        />
                        <label className="custom-control-label" htmlFor="site-all"></label>
                      </div>
                    </DataTableRow>
                    <DataTableRow>
                      <span className="sub-text">Site</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Code</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Ville</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Pays</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Statut</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="btn btn-xs btn-trigger btn-icon dropdown-toggle me-n1">
                          <Icon name="more-h"></Icon>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#remove"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                }}
                              >
                                <Icon name="trash"></Icon>
                                <span>Supprimer</span>
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </DataTableRow>
                  </DataTableHead>
                  {currentItems.map((item) => (
                    <DataTableItem key={item.id}>
                      <DataTableRow className="nk-tb-col-check">
                        <div className="custom-control custom-control-sm custom-checkbox notext">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            defaultChecked={item.checked}
                            id={item.id + "site"}
                            key={Math.random()}
                            onChange={(e) => onSelectChange(e, item.id)}
                          />
                          <label className="custom-control-label" htmlFor={item.id + "site"}></label>
                        </div>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-lead">{item.name || "Sans nom"}</span>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span className="tb-sub">{item.code || "-"}</span>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span className="tb-sub">{item.city || "-"}</span>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span className="tb-sub">{item.country || "-"}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className={`badge text-bg-${item.is_active ? "success" : "danger"}`}>
                          {item.is_active ? "Actif" : "Inactif"}
                        </span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <button
                              className="btn btn-trigger btn-icon"
                              onClick={() => onEditClick(item.id)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Modifier"
                            >
                              <Icon name="edit"></Icon>
                            </button>
                          </li>
                          <li className="nk-tb-action-hidden">
                            <button
                              className="btn btn-trigger btn-icon text-danger"
                              onClick={() => onDeleteClick(item.id)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Supprimer"
                            >
                              <Icon name="trash"></Icon>
                            </button>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              {filteredSites.length > 0 && (
                <div className="card-inner">
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={filteredSites.length}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </Block>

        {/* Modal de confirmation de suppression */}
        <Modal isOpen={deleteModal} toggle={onDeleteCancel} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Confirmer la suppression</h5>
            <button className="close" onClick={onDeleteCancel}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <p>Êtes-vous sûr de vouloir supprimer le site <strong>"{deleteName}"</strong> ?</p>
            <p className="text-soft small">Cette action est irréversible.</p>
            <div className="form-group text-end mt-4">
              <Button color="light" onClick={onDeleteCancel} className="me-2">
                Annuler
              </Button>
              <Button color="danger" onClick={onDeleteConfirm}>
                Supprimer
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Modal Nouveau site */}
        <Modal isOpen={modal} toggle={onFormCancel} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">{editId ? "Modifier" : "Nouveau"} site</h5>
            <button className="close" onClick={onFormCancel}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={handleSubmit(onFormSubmit)}>
              <div className="form-group">
                <label className="form-label">Nom du site *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  defaultValue={formData.name}
                  ref={register({ required: "Le nom du site est requis" })}
                />
                {errors.name && (
                  <div className="invalid-feedback d-block">{errors.name.message}</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Code</label>
                <input
                  type="text"
                  className="form-control"
                  name="code"
                  defaultValue={formData.code}
                  ref={register}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Adresse</label>
                <textarea
                  className="form-control"
                  name="address"
                  rows="2"
                  defaultValue={formData.address}
                  ref={register}
                />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Ville *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                      name="city"
                      defaultValue={formData.city}
                      ref={register({ required: "La ville est requise" })}
                    />
                    {errors.city && (
                      <div className="invalid-feedback d-block">{errors.city.message}</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Pays *</label>
                    <select
                      className={`form-select ${errors.country ? 'is-invalid' : ''}`}
                      name="country"
                      defaultValue={formData.country}
                      ref={register({ required: "Le pays est requis" })}
                    >
                      <option value="">Sélectionner un pays</option>
                      <option value="Congo Brazzaville">Congo Brazzaville</option>
                      <option value="Congo Kinshasa">Congo Kinshasa</option>
                      <option value="Cameroun">Cameroun</option>
                      <option value="Gabon">Gabon</option>
                      <option value="Guinée équatoriale">Guinée équatoriale</option>
                      <option value="Tchad">Tchad</option>
                      <option value="République centrafricaine">République centrafricaine</option>
                      <option value="Angola">Angola</option>
                      <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                      <option value="Sénégal">Sénégal</option>
                      <option value="Mali">Mali</option>
                      <option value="Burkina Faso">Burkina Faso</option>
                      <option value="Niger">Niger</option>
                      <option value="Togo">Togo</option>
                      <option value="Bénin">Bénin</option>
                      <option value="Guinée">Guinée</option>
                      <option value="Madagascar">Madagascar</option>
                      <option value="Mauritanie">Mauritanie</option>
                      <option value="Burundi">Burundi</option>
                      <option value="Rwanda">Rwanda</option>
                      <option value="Djibouti">Djibouti</option>
                      <option value="Comores">Comores</option>
                      <option value="Seychelles">Seychelles</option>
                      <option value="Maurice">Maurice</option>
                      <option value="Autre">Autre</option>
                    </select>
                    {errors.country && (
                      <div className="invalid-feedback d-block">{errors.country.message}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group mt-3">
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    name="is_active"
                    id="is_active"
                    defaultChecked={formData.is_active}
                    ref={register}
                  />
                  <label className="custom-control-label" htmlFor="is_active">
                    Site actif
                  </label>
                </div>
              </div>
              <div className="form-group text-end">
                <Button color="light" onClick={onFormCancel} className="me-2">
                  Annuler
                </Button>
                <Button color="primary" type="submit">
                  {editId ? "Modifier" : "Créer"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default SitesPage;

